import { useEffect } from 'react';
import { heapConfig } from '@api/auth/config';

declare global {
  interface HeapAnalyticsLoader extends Array<unknown> {
    load: (appid: string, config?: { forceSSL?: boolean; [k: string]: unknown }) => void;
    appid?: string;
    config?: Record<string, unknown>;
    [method: string]: unknown;
  }

  interface Window {
    heap?: HeapAnalyticsLoader;
  }
}

const HEAP_METHODS = [
  'addEventProperties',
  'addUserProperties',
  'clearEventProperties',
  'identify',
  'resetIdentity',
  'removeEventProperty',
  'setEventProperties',
  'track',
  'unsetEventProperty',
] as const;

function loadHeap(appid: string) {
  console.info('Loading Heap Analytics with appid:', appid);
  window.heap = window.heap || ([] as unknown as HeapAnalyticsLoader);
  const heap: HeapAnalyticsLoader = window.heap;

  heap.load = (id, config = {}) => {
    heap.appid = id;
    heap.config = config;

    const useSsl = config.forceSSL || document.location.protocol === 'https:';
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `${useSsl ? 'https' : 'http'}://cdn.heapanalytics.com/js/heap-${id}.js`;
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);

    HEAP_METHODS.forEach((name) => {
      heap[name] = function queued(...args: unknown[]) {
        heap.push([name, ...args]);
      };
    });
  };

  heap.load(appid);
}

export function Heap() {
  useEffect(() => {
    if (!heapConfig.envId) return;
    if (typeof window === 'undefined') return;
    if (window.heap?.appid) return;
    loadHeap(heapConfig.envId);
  }, []);

  return null;
}

type HeapMethod = (...args: unknown[]) => void;

function callHeap(name: (typeof HEAP_METHODS)[number], ...args: unknown[]) {
  if (typeof window === 'undefined') return;
  const fn = window.heap?.[name] as HeapMethod | undefined;
  fn?.apply(window.heap, args);
}

export function trackHeapEvent(event: string, properties?: Record<string, unknown>) {
  callHeap('track', event, properties);
}

export function identifyHeapUser(identity: string) {
  callHeap('identify', identity);
}

export function addHeapUserProperties(properties: Record<string, unknown>) {
  callHeap('addUserProperties', properties);
}

export function addHeapEventProperties(properties: Record<string, unknown>) {
  callHeap('addEventProperties', properties);
}

export function resetHeapIdentity() {
  callHeap('resetIdentity');
}
